/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable prefer-destructuring */
import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useReducer,
  useRef,
  useCallback,
} from 'react';
import moment from 'moment-timezone';
import {
  getVisitHopeAttributes,
  getCurrentMilestone,
  updateHopeVisitAttribute,
  updateVisitSFVRequired,
  isVisitSFVRequired,
  siaRequiresSfv,
  removeVisitAttribute,
  buildHopePatientDetailPayload,
  canAccptHopeVisitBeReopened,
  canDeclinedHopeVisitBeReopened,
  getHopeAttributes,
  getHopeMilestoneKeyStringFromReopenVisitAttributes,
} from '@mummssoftware/utils/hope';
import {
  getHeaders,
  getStartDate,
  delay,
  isSameTime,
  isAgencyEnabled,
} from '@mummssoftware/utils/utils';
import {
  getPatientInProgressVisit,
  getAllInProgPatientVisits,
} from '@mummssoftware/utils/pimReader';
import {
  isDrivingVisitExpired,
  getDrivingVisitRemainingMs,
} from '@mummssoftware/utils/timeUtils';
import {
  includedc,
  ProgressNotes as ProgressNotesWidget,
  SmartChartIPU,
  visitHeaderID,
} from '@mummssoftware/utils/constants';
import { formatPimDate, formatPostDate } from '@mummssoftware/utils/formatters';
import {
  GlobalContext,
  SET_APP_VISIT,
  SET_APP_VISIT_DETAILS,
  SET_LEVEL_OF_CARE,
  SET_PATIENT_STANDING_ORDERS,
  OPEN_WIDGET,
  REFRESH_PN_WIDGET,
  SET_GLOBAL_MEASURES,
  RELOAD_MANIFEST,
  RELOAD_MEASURES,
  SET_ATTEMPTED_VISIT_NOTE,
  priorVisit,
} from '@mummssoftware/global-context/GlobalContext';
import {
  VisitsGlobalContext,
  isVisitOKToDelete,
  SET_PATIENT_VISITS,
  SET_REFRESH_VISIT,
  SET_VISIT_TYPES_OPTIONS,
  SET_DRIVING_VISIT,
  SHOW_DRIVING_EXPIRED_MODAL,
  CLEAR_DRIVING_VISIT,
} from '@mummssoftware/global-context/VisitsGlobalContext';
import {
  PNGlobalContext,
  SET_PROGRESS_NOTES,
  REFRESH_PN_DATA,
  HopeGlobalContext,
} from '@mummssoftware/global-context';
import {
  TaskGlobalContext,
  UPDATE_WEEKLY_TASKS,
} from '@mummssoftware/global-context/TaskGlobalContext';
import {
  SET_NON_USER_IN_PROG_HOPE_VISIT,
  UPDATE_SIA_DATA,
  SET_SIA_TEXT,
  SET_SIA_DISPLAY,
  SET_HOPE_VISIT_ATTRIBUTES_SAVED,
  SET_IS_HOPE_VISIT,
  SET_HOPE_DETAIL,
} from '@mummssoftware/global-context/HopeGlobalContext';
import {
  lockPatientInPim,
  unlockPatientInPim,
  notifyPimVisitPutSuccess,
  initDocumentVisitSwitchListener,
} from '@mummssoftware/pim-messenger';
import {
  getDurationMinutesFromVisitSources,
  minutesToTimerHhMmParts,
} from '@mummssoftware/utils/visitUtils';
import {
  getSIANote,
  PersistHopeVisitAttributesArgs,
} from '../../HOPE/hopeVisitPersistence';
import {
  milestoneQuestionNumber,
  SFVQuestionNumber,
} from '../../HOPE/hopeConstants';
import { isTaskTicked } from '../../Tasks/utils';
import { DrivingVisitExpiredModal } from '../../DrivingVisit/DrivingVisitExpiredModal';
import {
  OPEN_VISIT_WIDGET,
  GET_ROLE_GROUPS,
  SET_ACTING_AS_ROLE,
  SET_PREP_VISIT,
  SET_SAVED_VISIT,
  GET_TIME,
  SET_TIME,
  SET_EDIT_TIME,
  SET_CC_TIME,
  SET_TRAVEL_TIME,
  SET_TRANSIT_FEES,
  SET_TOLLS_FEES,
  SET_MILEAGE,
  MINIMIZE_VISIT_DIALOG,
  SET_VISIT_TITLE,
  SET_VISIT_ERROR,
  SHOW_TASKS,
  SET_VISIT_ALERT,
  InProgress,
  InProgressDriving,
  Completed,
  LOCMap,
  SET_IPU_VISIT,
  SET_IPU_VISIT_CREATED,
  SET_FACE_TO_FACE,
  SET_IPU_RADIO_CHOICE,
  SET_INITIAL_SAVED_VISIT,
  SHOW_CONFIRM_VISIT,
  STOP_VISIT,
  ADD_TASK,
  DELETE_TASK,
  RESET_TASKS,
  SET_HOPE_VISIT_WARNING,
  SET_VISIT_EPISODE,
  HYDRATE_EDIT_FIELDS_FROM_VISIT,
  SET_OPENED_FROM_VISIT_LOG_REOPEN,
  SET_DOCUMENT_SESSION_STARTED_AT,
  SET_CLOSING_PRIOR_VISIT,
} from '../constants';
import { Context } from '../../../context';
import { getUserTasks } from '../visit/visitUtils';
import defaults from '../../../translations/en.json';

/** Passed to `hydrateDocumentVisitFromLog` — cold load adds existing PIM `documentationTime` on PUT. */
export type HydrateDocumentVisitOptions = {
  fromColdLoad?: boolean;
};

type VisitContextProviderProps = {
  children: ReactNode;
};

type VisitContext = {
  visitOpen: boolean;
  roleGroups: any | null;
  dispatchVisit?: any;
  prepVisit: any;
  savedVisit: mumms.PatientVisit | null;
  initSavedVisit: mumms.PatientVisit | null;
  timeRequest: boolean;
  timerDuration: string[];
  numPatientTasks?: number;
  closePatientVisit: (
    patientNumber: string,
    patientVisit: any,
    hcpcsDTO?: mumms.HCPCSDTO[],
  ) => Promise<any>;
  savePatientAdminTask: (adminTasks: mumms.AdminSubTask[]) => Promise<boolean>;
  deletePatientAdminTask: (id: string) => Promise<boolean>;
  updatePatientVisit: (
    patientNumber: string,
    patientVisit: any,
    // @note when able to re-build HCPCS
    hcpcsDTO?: mumms.HCPCSDTO[],
  ) => Promise<boolean>;
  saveIPUVisitNote: (res: any) => Promise<void>;
  processAttemptedVisit: () => void;
  reset: () => void;
  primaryRole: string | null;
  actingAsRole: string | null;
  travelMileage: number | null;
  transitFees: number | null;
  travelDuration: number | null;
  tollsFee: number | null;
  visitStartTimer: boolean;
  minimizeVisitDialog: boolean;
  visitTitle: string | undefined | Element;
  editTime: string[] | null;
  ccTime: string[] | null;
  visitError: boolean;
  userTasks: mumms.PatientCppTask[] | null;
  showTasks: boolean;
  alertVisitInfo: mumms.ErrorInfo | null;
  showOverlay: boolean;
  IPUVisit: boolean;
  editFaceToFace: boolean | null;
  ipuRadioChoice: null | string;
  canDeleteOpenVisit: () => Promise<boolean>;
  deleteOpenVisit: () => Promise<boolean>;
  is30MinAgency: boolean | null | '';
  visitTypeOptions: any;
  showConfirmVisit: boolean;
  confirmVisitMissingDoc: boolean;
  confirmVisitMissingHCPCS: boolean;
  stopVisit: boolean;
  tasksToBeSaved: Record<string, mumms.AdminSubTask>;
  tasksToBeDeleted: Record<string, mumms.AdminSubTask>;
  checkforOtherUserHopeVisits: () => any;
  persistHopeVisitAttributes: (
    args: PersistHopeVisitAttributesArgs,
  ) => Promise<boolean>;
  hopeVisitWarning: any;
  visitEpisode: mumms.episode | undefined | null;
  /** After visit-log reopens a visit: opens visit widget with PIM DTO + pimreader row (like `loadInProgress`). */
  hydrateInProgressVisitFromLog?: (
    pimreaderVisit: any,
    pimPatientVisitDto: any,
    hopeReopenWorkflowMilestone?: string,
  ) => void;
  /** After visit-log "Document": same hydrate as reopen + `priorVisit` LOC for PriorVisit UI (no reopen loader flag). */
  hydrateDocumentVisitFromLog?: (
    pimreaderVisit: any,
    pimPatientVisitDto: any,
    options?: HydrateDocumentVisitOptions,
  ) => void;
  openedFromVisitLogReopen: boolean;
  /** Minutes elapsed in the current document-on-visit session (same-session v1). */
  getDocumentSessionMinutes: () => number;
  /**
   * Minutes for document-visit PUT payloads.
   * Fresh session: session slice only. Cold load: existing `documentationTime` + session slice.
   */
  getDocumentationTimeForPut: () => number;
  closeDocumentingVisit: () => Promise<void>;
  closingPriorVisit: boolean;
};

type VisitContextState = {
  visitOpen: boolean;
  roleGroups: any | null;
  dispatchVisit?: any;
  prepVisit: any;
  savedVisit: mumms.PatientVisit | null;
  initSavedVisit: mumms.PatientVisit | null;
  timeRequest: boolean;
  timerDuration: string[];
  actingAsRole: string | null;
  travelMileage: number | null;
  transitFees: number | null;
  travelDuration: number | null;
  tollsFee: number | null;
  visitStartTimer: boolean;
  minimizeVisitDialog: boolean;
  visitTitle: string | undefined | Element;
  editTime: string[] | null;
  ccTime: string[] | null;
  visitError: boolean;
  showTasks: boolean;
  alertVisitInfo: mumms.ErrorInfo | null;
  IPUVisit: boolean;
  editFaceToFace: boolean | null;
  ipuRadioChoice: null | string;
  showConfirmVisit: boolean;
  confirmVisitMissingDoc: boolean;
  confirmVisitMissingHCPCS: boolean;
  stopVisit: boolean;
  tasksToBeSaved: Record<string, mumms.AdminSubTask>;
  tasksToBeDeleted: Record<string, mumms.AdminSubTask>;
  hopeVisitWarning: any;
  visitEpisode: mumms.episode | undefined | null;
  /** True after visit-log reopen hydrates until RecordVisit finishes loading details. */
  openedFromVisitLogReopen: boolean;
  documentSessionStartedAt: number | null;
  closingPriorVisit: boolean;
  getDocumentSessionMinutes: any;
};

const initialState = {
  visitOpen: false,
  themeOverlay: false,
  idgMeetingContent: null,
  widgetsReady: [],
  roleGroups: null,
  prepVisit: null,
  savedVisit: null,
  initSavedVisit: null,
  timeRequest: false,
  timerDuration: ['00', '00'],
  actingAsRole: null,
  travelMileage: null,
  transitFees: null,
  travelDuration: null,
  tollsFee: null,
  visitStartTimer: false,
  minimizeVisitDialog: false,
  visitTitle: defaults.createVisit,
  editTime: null,
  ccTime: null,
  visitError: false,
  showTasks: false,
  alertVisitInfo: null,
  IPUVisit: false,
  editFaceToFace: null,
  ipuRadioChoice: null,
  showConfirmVisit: false,
  confirmVisitMissingDoc: false,
  confirmVisitMissingHCPCS: false,
  stopVisit: false,
  hopeVisitWarning: null,
  tasksToBeSaved: {},
  tasksToBeDeleted: {},
  visitEpisode: null,
  openedFromVisitLogReopen: false,
  documentSessionStartedAt: null,
  closingPriorVisit: false,
  getDocumentSessionMinutes: () => 0,
  getDocumentationTimeForPut: () => 0,
  closeDocumentingVisit: () => Promise.resolve(),
};

const initVisitContext = {
  visitOpen: false,
  roleGroups: null,
  prepVisit: null,
  savedVisit: null,
  initSavedVisit: null,
  timeRequest: false,
  timerDuration: ['00', '00'],
  actingAsRole: null,
  travelMileage: null,
  transitFees: null,
  travelDuration: null,
  tollsFee: null,
  visitStartTimer: false,
  minimizeVisitDialog: false,
  visitTitle: defaults.createVisit,
  editTime: null,
  ccTime: null,
  visitError: false,
  showTasks: false,
  alertVisitInfo: null,
  IPUVisit: false,
  editFaceToFace: null,
  ipuRadioChoice: null,
  showConfirmVisit: false,
  confirmVisitMissingDoc: false,
  confirmVisitMissingHCPCS: false,
  stopVisit: false,
  tasksToBeSaved: {},
  tasksToBeDeleted: {},
  hopeVisitWarning: null,
  visitEpisode: null,
  openedFromVisitLogReopen: false,
  documentSessionStartedAt: null,
  closingPriorVisit: false,
  getDocumentSessionMinutes: () => 0,
  getDocumentationTimeForPut: () => 0,
  closeDocumentingVisit: () => Promise.resolve(),
};

export const VisitContextReducer = (
  state: VisitContextState,
  action: { type: string; payload: mumms.ComponentName | any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case OPEN_VISIT_WIDGET:
      newState.visitOpen = action.payload;
      break;
    case GET_ROLE_GROUPS:
      newState.roleGroups = action.payload;
      break;
    case SET_PREP_VISIT:
      newState.prepVisit = action.payload;
      break;
    case SET_SAVED_VISIT:
      newState.savedVisit = action.payload;
      break;
    // initial saved visit to get the initial visit time
    case SET_INITIAL_SAVED_VISIT:
      newState.initSavedVisit = action.payload;
      break;
    case GET_TIME:
      newState.timeRequest = action.payload;
      break;
    case SET_TIME:
      newState.timerDuration = action.payload;
      break;
    // Timer/fees seed from visit payload; see provider effect + lastHydratedAppVisitIdRef.
    case HYDRATE_EDIT_FIELDS_FROM_VISIT: {
      const sv = action.payload?.savedVisit as mumms.PatientVisit | null;
      const av = action.payload?.appVisit as mumms.Visit | undefined | null;
      if (!sv) {
        break;
      }
      const durationMins = getDurationMinutesFromVisitSources(sv, av);
      const timerUnset =
        !newState.timerDuration ||
        (newState.timerDuration[0] === '00' &&
          newState.timerDuration[1] === '00');
      if (durationMins != null && timerUnset) {
        const pair = minutesToTimerHhMmParts(durationMins);
        newState.timerDuration = pair;
        newState.editTime = pair;
      }
      const seedNum = (cur: number | null, raw: unknown) => {
        if (cur != null) return cur;
        if (raw == null || raw === '') return cur;
        const n = Number(raw);
        return Number.isNaN(n) ? cur : n;
      };
      newState.travelMileage = seedNum(
        newState.travelMileage,
        sv.travelMileage,
      );
      newState.transitFees = seedNum(newState.transitFees, sv.transitFees);
      newState.travelDuration = seedNum(
        newState.travelDuration,
        sv.travelDuration,
      );
      newState.tollsFee = seedNum(newState.tollsFee, sv.tolls);
      break;
    }
    case SET_EDIT_TIME:
      newState.editTime = action.payload;
      break;
    case SET_CC_TIME:
      newState.ccTime = action.payload;
      break;
    case SET_MILEAGE:
      newState.travelMileage = action.payload;
      break;
    case SET_TOLLS_FEES:
      newState.tollsFee = action.payload;
      break;
    case SET_TRANSIT_FEES:
      newState.transitFees = action.payload;
      break;
    case SET_TRAVEL_TIME:
      newState.travelDuration = action.payload;
      break;
    case SET_ACTING_AS_ROLE:
      newState.actingAsRole = action.payload;
      break;
    case MINIMIZE_VISIT_DIALOG:
      newState.minimizeVisitDialog = action.payload;
      break;
    case SET_VISIT_TITLE:
      newState.visitTitle = action.payload;
      break;
    case SET_VISIT_ERROR:
      newState.visitError = action.payload;
      break;
    case SHOW_TASKS:
      newState.showTasks = action.payload;
      break;
    case SET_VISIT_ALERT:
      newState.alertVisitInfo = action.payload;
      break;
    case SET_IPU_VISIT:
      newState.IPUVisit = action.payload;
      break;
    case SET_FACE_TO_FACE:
      newState.editFaceToFace = action.payload;
      break;
    case RESET_TASKS:
      newState.tasksToBeSaved = {};
      newState.tasksToBeDeleted = {};
      break;
    case SET_IPU_RADIO_CHOICE:
      newState.ipuRadioChoice = action.payload;
      break;
    case SET_HOPE_VISIT_WARNING:
      newState.hopeVisitWarning = action.payload;
      break;
    case STOP_VISIT:
      newState.stopVisit = action.payload;
      break;
    case SHOW_CONFIRM_VISIT:
      if (action.payload === false) {
        newState.showConfirmVisit = false;
        newState.confirmVisitMissingDoc = false;
        newState.confirmVisitMissingHCPCS = false;
      } else if (action.payload === true) {
        newState.showConfirmVisit = true;
      } else {
        newState.showConfirmVisit = action.payload.open;
        newState.confirmVisitMissingDoc = action.payload.missingDoc ?? false;
        newState.confirmVisitMissingHCPCS =
          action.payload.missingHCPCS ?? false;
      }
      break;
    case ADD_TASK:
      {
        const { tasksToBeSaved } = newState;
        const { id, task } = action.payload;
        if (tasksToBeSaved[id]) {
          delete tasksToBeSaved[id];
        } else {
          tasksToBeSaved[id] = task;
        }
      }
      break;
    case DELETE_TASK:
      {
        const { tasksToBeDeleted } = newState;
        const { id, task } = action.payload;
        if (tasksToBeDeleted[id]) {
          delete tasksToBeDeleted[id];
        } else {
          tasksToBeDeleted[id] = task;
        }
      }
      break;
    case SET_VISIT_EPISODE:
      newState.visitEpisode = action.payload;
      break;
    case SET_OPENED_FROM_VISIT_LOG_REOPEN:
      newState.openedFromVisitLogReopen = action.payload;
      break;
    case SET_DOCUMENT_SESSION_STARTED_AT:
      newState.documentSessionStartedAt = action.payload;
      break;
    case SET_CLOSING_PRIOR_VISIT:
      newState.closingPriorVisit = action.payload;
      break;
    default:
      newState = state;
      break;
  }

  return newState;
};
export const VisitContext = createContext<VisitContext>(
  (initVisitContext as unknown) as VisitContext,
);
export const VisitContextProvider = ({
  children,
}: VisitContextProviderProps) => {
  const [
    {
      visitOpen,
      prepVisit,
      actingAsRole,
      savedVisit,
      initSavedVisit,
      timeRequest,
      timerDuration,
      travelMileage,
      transitFees,
      travelDuration,
      tollsFee,
      visitStartTimer,
      minimizeVisitDialog,
      visitTitle,
      editTime,
      ccTime,
      visitError,
      showTasks,
      alertVisitInfo,
      IPUVisit,
      editFaceToFace,
      ipuRadioChoice,
      showConfirmVisit,
      confirmVisitMissingDoc,
      confirmVisitMissingHCPCS,
      stopVisit,
      tasksToBeSaved,
      tasksToBeDeleted,
      hopeVisitWarning,
      visitEpisode,
      openedFromVisitLogReopen,
      documentSessionStartedAt,
      closingPriorVisit,
    },
    dispatch,
  ] = useReducer(VisitContextReducer, initialState);
  const {
    pim,
    observation,
    currentUser,
    keycloak,
    patient,
    agency,
    isMummsAdmin,
    siteId,
    visitAttributes,
    workflow,
  } = useContext(Context);
  const {
    patientStandingOrdersGlobal,
    widgetNamesOpen,
    widgetsOpen,
    appVisit,
    levelOfCare,
    dispatch: dispatchGlobal,
  } = useContext(GlobalContext);

  /**
   * Tracks which `appVisit.id` we last ran HYDRATE_EDIT_FIELDS_FROM_VISIT for.
   * `savedVisit` / `appVisit` objects are often new references on each render; comparing ids
   * avoids repeating hydration. When the visit widget closes or `appVisit` clears, reset so a
   * later open can hydrate again. When `appVisit.id` changes (new visit, reopen from log, etc.),
   * ref !== id and we dispatch once for that visit.
   */
  const lastHydratedAppVisitIdRef = useRef<unknown>(undefined);
  /** True when this document session was restored on dashboard load (add GET baseline on PUT). */
  const documentVisitFromColdLoadRef = useRef(false);
  /** Tail of the visits PUT queue (see `enqueueVisitPut`). */
  const visitPutQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  /**
   * Runs visits PUTs one at a time, so a mid-visit save (e.g. SFVRQ on SIA OK)
   * cannot land after Submit Visit and put the visit back to In Progress.
   */
  function enqueueVisitPut<T>(put: () => Promise<T>): Promise<T> {
    const run = visitPutQueueRef.current.then(put, put);
    visitPutQueueRef.current = run.catch(() => undefined);
    return run;
  }

  useEffect(() => {
    if (!visitOpen || appVisit?.id == null) {
      lastHydratedAppVisitIdRef.current = undefined;
      return;
    }
    // PIM `savedVisit` must be for the same visit as global `appVisit` (merged row + DTO).
    if (
      savedVisit?.id == null ||
      String(savedVisit.id) !== String(appVisit.id)
    ) {
      return;
    }
    if (lastHydratedAppVisitIdRef.current === appVisit.id) {
      return;
    }
    lastHydratedAppVisitIdRef.current = appVisit.id;
    dispatch({
      type: HYDRATE_EDIT_FIELDS_FROM_VISIT,
      payload: { savedVisit, appVisit },
    });
  }, [visitOpen, savedVisit, appVisit, dispatch]);

  const { dispatchTask } = useContext(TaskGlobalContext);
  const hopeGlobalContext = useContext(HopeGlobalContext);
  const {
    isOtherInProgHopeVisit,
    isHopeVisit,
    hopeVisitAttributesSaved,
  } = hopeGlobalContext;
  const globalContext = useContext(GlobalContext);
  const visitsGlobalContext = useContext(VisitsGlobalContext);
  const {
    globalDrivingVisit,
    drivingExpiredModalOpen,
    dispatchVG,
  } = visitsGlobalContext;
  const pnGlobalContext = useContext(PNGlobalContext);
  const { progressNotesGlobal } = useContext(PNGlobalContext);

  const [roleGroupOptions, setRoleGroups] = useState<any>(null);
  const [visitOptions, setVisitOptions] = useState<any>(null);
  const [primaryRole, setPrimaryRole] = useState<any>(null);

  const [userTasks, setUserTasks] = useState<mumms.PatientCppTask[] | null>(
    null,
  );
  const [numTasks, setNumTasks] = useState<number>(0);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [deletingDrivingVisit, setDeletingDrivingVisit] = useState<boolean>(
    false,
  );

  const tz = moment.tz.guess();
  const siteTimezone: any = currentUser ? currentUser.siteTimezone : tz;

  // 15 min increment for selected agencies
  const increment30MinVisitBins: string = window.env
    ?.increment30MinVisitBins as string;
  const is30MinAgency = isAgencyEnabled(increment30MinVisitBins, currentUser);

  const getVisitOptions = async () => {
    // sync issue with getRolesOptions
    if (!visitOptions) {
      const visitoptions: any = await pim.query(
        'visittype_vw_v3',
        {
          select: `*`,
        },
        'pimreader',
      );

      if (!(visitoptions instanceof Response)) {
        const filterrolesoptions = [...visitoptions].filter(
          (r: any) => r.issmartchart && r.ispatient,
        );

        const roletypeOptions = filterrolesoptions.map((r: any) => ({
          label: r.name,
          value: r.code,
          id: r.id,
        }));

        setVisitOptions(roletypeOptions);
        visitsGlobalContext.dispatchVG({
          type: SET_VISIT_TYPES_OPTIONS,
          payload: roletypeOptions,
        });
      } else {
        setVisitOptions([]);
        visitsGlobalContext.dispatchVG({
          type: SET_VISIT_TYPES_OPTIONS,
          payload: [],
        });
      }
    }
  };

  const getRolesOptions = async () => {
    if (!roleGroupOptions) {
      const roles: any = await pim.query(
        'rankedpersonroles_vw',
        {
          select: `*`,
          person_id: `eq.${currentUser.userId}`,
        },
        'pimreader',
      );

      if (!(roles instanceof Response) && (roles as any).length) {
        setPrimaryRole(roles[0].rolename);
        const rolesOptions = roles
          .filter((r: any) => r.roleshortname)
          .map((r: any) => ({
            label: r.rolename,
            value: r.rolename,
            id: r.role_id,
            ...r,
          }));
        setRoleGroups(rolesOptions);
      } else {
        setRoleGroups([]);
      }
    }
  };

  const deleteOpenVisit = async () => {
    const okToDelete = await canDeleteOpenVisit();
    if (okToDelete && appVisit && patient?.patientNumber) {
      const response = await pim.deletePatientVisit(
        patient?.patientNumber,
        appVisit?.id,
        agency,
      );
      if (!response || (response instanceof Response && !response?.ok)) {
        // raise an error message toast
        return !okToDelete;
      }
      reset();
    }
    return okToDelete;
  };
  //
  const canDeleteOpenVisit = async () => {
    if (appVisit && currentUser) {
      const personNumber = currentUser.personNumber || '';
      return isVisitOKToDelete(appVisit, keycloak, personNumber, agency);
    }
    return false;
  };

  const withLOC = (dto: any) =>
    dto
      ? {
          ...dto,
          levelofcareshortname: (LOCMap as any)[(dto as any).levelofcare],
        }
      : null;

  /**
   * PIM visit DTOs carry startDate, while appVisit consumers (progress note,
   * measure, and form saves) read effectivedatetime. Derive it so a visit update
   * cannot leave appVisit with an id but no visit date, which would save a note
   * with patientvisit_id set and visitdatetime null.
   */
  const withEffectiveDatetime = (dto: any) =>
    dto && !dto.effectivedatetime && dto.startDate
      ? { ...dto, effectivedatetime: formatPimDate(dto.startDate) }
      : dto;

  const loadInProgress = (
    priorSavedVisit: any,
    toSaveVisit: any,
    hopeReopenWorkflowMilestone?: string,
  ) => {
    if (!priorSavedVisit.visitAttributes && toSaveVisit.visitAttributes) {
      priorSavedVisit.visitAttributes = [...toSaveVisit.visitAttributes];
    }
    priorSavedVisit.careProviderRoleGroup = toSaveVisit.careProviderRoleGroup;
    dispatch({
      type: OPEN_VISIT_WIDGET,
      payload: true,
    });
    dispatch({
      type: SET_IPU_VISIT,
      payload: toSaveVisit.source === SmartChartIPU,
    });
    const mergedVisit = { ...priorSavedVisit, ...toSaveVisit };
    globalContext.dispatch({
      type: SET_APP_VISIT,
      payload:
        hopeReopenWorkflowMilestone != null &&
        hopeReopenWorkflowMilestone !== ''
          ? {
              ...mergedVisit,
              hopeReopenWorkflowMilestone,
            }
          : mergedVisit,
    });
    globalContext.dispatch({
      type: SET_APP_VISIT_DETAILS,
      payload: priorSavedVisit,
    });
    dispatch({
      type: SET_SAVED_VISIT,
      payload: toSaveVisit,
    });
    dispatch({
      type: SET_INITIAL_SAVED_VISIT,
      payload: toSaveVisit,
    });
  };

  const handleDrivingVisit = (priorSavedVisit: any) => {
    const startDrivingTime = priorSavedVisit?.startdrivingtime;

    visitsGlobalContext.dispatchVG({
      type: SET_DRIVING_VISIT,
      payload: priorSavedVisit,
    });

    if (!startDrivingTime || isDrivingVisitExpired(startDrivingTime)) {
      visitsGlobalContext.dispatchVG({
        type: SHOW_DRIVING_EXPIRED_MODAL,
      });
    }
  };

  const confirmDeleteExpiredDrivingVisit = async () => {
    if (deletingDrivingVisit) return;
    if (!patient?.patientNumber || !globalDrivingVisit?.id) return;

    setDeletingDrivingVisit(true);
    const response = await pim.deletePatientVisit(
      patient.patientNumber,
      globalDrivingVisit.id,
      agency,
    );

    setDeletingDrivingVisit(false);

    if (!response || (response instanceof Response && !response.ok)) {
      dispatch({
        type: SET_VISIT_ALERT,
        payload: {
          text: defaults.deleteVisitErrorMsg,
          severity: 'error',
        },
      });
      return;
    }

    dispatchVG({ type: CLEAR_DRIVING_VISIT });
    dispatchVG({ type: SET_REFRESH_VISIT, payload: null });
  };

  /**
   * Why this exists
   * ---------------
   * `hopeReopenWorkflowMilestone` is a UI-only marker stitched onto `appVisit`
   * by the app — it is **never** present on any PIM payload. It is produced
   * once, in visit-log, when the user clicks "Reopen Visit"
   * (`VisitLogDetailRow` -> `getHopeMilestoneKeyStringFromReopenVisitAttributes`)
   * and then carried on `appVisit` until the visit is closed.
   *
   * Before (without this derive function):
   *   - On cold dashboard load with an in-progress visit, `loadInProgress(...)`
   *     was called with no `hopeReopenWorkflowMilestone` argument.
   *   - `HopeGlobalContext.getVisitHopeStatus` determined the milestone
   *     "from scratch" — only from `patientHopeDetail` rows + visit attrs at
   *     the moment the widget rendered. No assumption was baked into hydration.
   *   - Side effect: a refresh on an in-progress *reopen* visit lost the
   *     reopen marker and `getVisitHopeStatus` walked the new-visit branch.
   *
   * Now (with this derive function):
   *   - Before calling `loadInProgress`, we read `visitattrsumdto`, run
   *     `getHopeAttributes`, and — if one of the accepted/refused HOPE
   *     shortnames passes `canAccptHopeVisitBeReopened` /
   *     `canDeclinedHopeVisitBeReopened` — we pre-compute a
   *     `hopeReopenWorkflowMilestone` from the attrs and pass it into
   *     `hydrateInProgressVisitFromLog` -> `loadInProgress`.
   *   - Net effect: the in-progress hydrate is now opinionated about reopen
   *     milestones, derived from the visit's own attrs. This recreates the
   *     same marker visit-log produced when the user originally clicked
   *     Reopen, so `getVisitHopeStatus` takes the reopen branch after refresh.
   *
   * The predicates and helper used here mirror visit-log's reopen logic, so
   * the cold-reload classification matches the visit-log row exactly.
   */
  const deriveHopeReopenWorkflowMilestoneForInProgressHydrate = useCallback(
    async (visitForAttrs: any): Promise<string | undefined> => {
      const attrField = visitForAttrs?.visitattrsumdto;
      if (!attrField || !attrField.length || !patient?.id || !workflow) {
        return undefined;
      }
      const [acceptedAtts, refusedHopeAtts] = getHopeAttributes(attrField);
      if (!acceptedAtts?.length && !refusedHopeAtts?.length) return undefined;

      // Read-first: if HopeGlobalContext already hydrated patientHopeDetail
      // (e.g. HopeWidgetContext fetched it on mount), reuse it instead of
      // hitting `getHopePatientDetails` a second time.
      const cachedHopeRows = hopeGlobalContext.patientHopeDetail;
      let hopeRows: mumms.HopeEvent[];
      if (Array.isArray(cachedHopeRows) && cachedHopeRows.length) {
        hopeRows = cachedHopeRows as mumms.HopeEvent[];
      } else {
        const hopeDetailData = await workflow.getHopePatientDetails(
          `${patient.id}`,
          `${(patient?.episodes?.length || 1) - 1}`,
          agency || '',
          siteId || '',
        );
        if (
          hopeDetailData instanceof Response ||
          !Array.isArray(hopeDetailData) ||
          !hopeDetailData.length
        ) {
          return undefined;
        }
        hopeRows = buildHopePatientDetailPayload(hopeDetailData);
        // Hydrate HopeGlobalContext so HopeWidgetContext's own fetch effect
        // short-circuits on `!patientHopeDetail`.
        hopeGlobalContext.dispatch({
          type: SET_HOPE_DETAIL,
          payload: hopeRows,
        });
      }
      const hopePatientRow = hopeRows[hopeRows.length - 1] as mumms.JSONObject;
      if (!hopePatientRow) return undefined;

      const acceptedAttsObj = acceptedAtts?.length
        ? acceptedAtts[0]
        : undefined;
      const refusedShortname = refusedHopeAtts[0]?.shortname;

      const canAccpt =
        acceptedAttsObj &&
        canAccptHopeVisitBeReopened(
          acceptedAttsObj.shortname,
          hopePatientRow,
        ) === true;
      const canDeclined =
        refusedShortname &&
        canDeclinedHopeVisitBeReopened(refusedShortname, hopePatientRow) ===
          true;

      if (!canAccpt && !canDeclined) return undefined;

      const m = getHopeMilestoneKeyStringFromReopenVisitAttributes(
        acceptedAtts,
        refusedHopeAtts,
      );
      return m || undefined;
    },
    [
      patient,
      workflow,
      agency,
      siteId,
      hopeGlobalContext.dispatch,
      hopeGlobalContext.patientHopeDetail,
    ],
  );

  /**
   * `hydrateInProgressVisitFromLog` / `hydrateDocumentVisitFromLog` are passed
   * to visit-log and memoized with minimal deps so visit-log handlers stay
   * stable. `loadInProgress` is a plain helper (not `useCallback`); the ref
   * forwards to the latest implementation without listing it in those deps.
   */
  const loadInProgressRef = useRef(loadInProgress);
  loadInProgressRef.current = loadInProgress;

  const hydrateInProgressVisitFromLog = useCallback(
    (
      pimreaderVisit: any,
      pimPatientVisitDto: any,
      hopeReopenWorkflowMilestone?: string,
    ) => {
      dispatch({
        type: SET_OPENED_FROM_VISIT_LOG_REOPEN,
        payload: true,
      });
      const prior = withLOC(pimreaderVisit);
      loadInProgressRef.current(
        prior,
        pimPatientVisitDto,
        hopeReopenWorkflowMilestone,
      );
    },
    [dispatch],
  );

  const hydrateDocumentVisitFromLog = useCallback(
    (
      pimreaderVisit: any,
      pimPatientVisitDto: any,
      options?: HydrateDocumentVisitOptions,
    ) => {
      documentVisitFromColdLoadRef.current = options?.fromColdLoad === true;
      const prior = withLOC(pimreaderVisit);
      loadInProgressRef.current(prior, pimPatientVisitDto, undefined);
      dispatchGlobal({
        type: SET_LEVEL_OF_CARE,
        payload: priorVisit,
      });
      dispatch({
        type: SET_DOCUMENT_SESSION_STARTED_AT,
        payload: Date.now(),
      });
      if (patient?.patientNumber != null && patient?.id != null) {
        lockPatientInPim({
          patientNumber: patient.patientNumber,
          patientId: patient.id,
        });
      }
    },
    [dispatch, dispatchGlobal, patient?.patientNumber, patient?.id],
  );

  const getDocumentSessionMinutes = useCallback(() => {
    if (documentSessionStartedAt == null) {
      return 0;
    }
    return Math.max(
      0,
      Math.round((Date.now() - documentSessionStartedAt) / (60 * 1000)),
    );
  }, [documentSessionStartedAt]);

  const getDocumentationTimeForPut = useCallback(() => {
    const sessionMinutes = getDocumentSessionMinutes();
    if (!documentVisitFromColdLoadRef.current) {
      return sessionMinutes;
    }
    return (savedVisit?.documentationTime ?? 0) + sessionMinutes;
  }, [getDocumentSessionMinutes, savedVisit?.documentationTime]);

  const isDocumentingVisit =
    levelOfCare === priorVisit && documentSessionStartedAt != null;

  const saveDocumentationTimeOnPendingSwitch = useCallback(() => {
    if (!isDocumentingVisit || !savedVisit?.id || !patient?.patientNumber) {
      return;
    }

    pim
      .updatePatientVisit(
        patient.patientNumber,
        {
          ...savedVisit,
          status: InProgress,
          reOpened: true,
          documentationTime: getDocumentationTimeForPut(),
        } as any,
        agency,
      )
      .catch(() => {
        // PIM still shows the switch modal; user can retry Leave.
      });

    if (patient?.patientNumber != null && patient?.id != null) {
      notifyPimVisitPutSuccess({
        patientNumber: patient.patientNumber,
        patientId: patient.id,
      });
    }
  }, [
    agency,
    getDocumentationTimeForPut,
    isDocumentingVisit,
    patient?.id,
    patient?.patientNumber,
    pim,
    savedVisit,
  ]);

  useEffect(() => {
    if (!isDocumentingVisit) {
      return undefined;
    }
    return initDocumentVisitSwitchListener(
      saveDocumentationTimeOnPendingSwitch,
    );
  }, [isDocumentingVisit, saveDocumentationTimeOnPendingSwitch]);

  const isDocumentingReopenedVisit = (visit: any) =>
    visit?.reOpened === true || visit?.reopened === true;

  const checkVisitActive = async (
    priorSavedVisit: any,
    visitstatus: string,
  ) => {
    if (priorSavedVisit && priorSavedVisit.status === InProgressDriving) {
      handleDrivingVisit(priorSavedVisit);
    } else if (priorSavedVisit && priorSavedVisit.status === InProgress) {
      const res = await pim.getVisitId(
        patient?.id as any,
        priorSavedVisit.id as any,
        agency,
      );
      if (!(res instanceof Response)) {
        const toSaveVisit = res;
        if (
          isDocumentingReopenedVisit(toSaveVisit) ||
          isDocumentingReopenedVisit(priorSavedVisit)
        ) {
          hydrateDocumentVisitFromLog(priorSavedVisit, toSaveVisit, {
            fromColdLoad: true,
          });
          return;
        }
        const visitForHopePredicates = {
          ...priorSavedVisit,
          visitattrsumdto:
            toSaveVisit?.visitattrsumdto ?? toSaveVisit?.visitAttributes,
        };
        const hopeReopenWorkflowMilestone = await deriveHopeReopenWorkflowMilestoneForInProgressHydrate(
          visitForHopePredicates,
        );
        loadInProgress(
          priorSavedVisit,
          toSaveVisit,
          hopeReopenWorkflowMilestone,
        );
      }
    }
  };

  const checkforOtherUserHopeVisits = async () => {
    let inProg = false;
    if (patient) {
      const priorVisitRes = await getAllInProgPatientVisits(
        agency,
        keycloak,
        patient,
      );
      if (!(priorVisitRes instanceof Response)) {
        if (priorVisitRes && priorVisitRes.length) {
          // todo if api error do not allow for hope visit
          // check for HOPE visit in all In progress visits

          const isHopeInProg = priorVisitRes
            .filter((x: any) => x.person_id !== currentUser.userId)
            .some((x: any) => getVisitHopeAttributes(x.visitattrsumdto));
          if (isHopeInProg) {
            hopeGlobalContext.dispatch({
              type: SET_NON_USER_IN_PROG_HOPE_VISIT,
              payload: true,
            });
            inProg = true;
          } else {
            hopeGlobalContext.dispatch({
              type: SET_NON_USER_IN_PROG_HOPE_VISIT,
              payload: false,
            });
          }
        }
      } else {
        // disptach error to client about visit
        // prevent setting hope visit
        dispatch({
          type: SET_VISIT_ALERT,
          payload: {
            text: defaults.visitError,
            severity: 'error',
          },
        });
        hopeGlobalContext.dispatch({
          type: SET_NON_USER_IN_PROG_HOPE_VISIT,
          payload: true,
        });
        inProg = true;
      }
      return inProg;
    }
    return false;
  };
  /* 
  Called on mount with visitstatus="In Progress" or "In Progress Driving".
  In Progress opens the visit widget via loadInProgress; In Progress Driving
  stores globalDrivingVisit and shows the expired modal when startDrivingTime >= 2h.
   */
  // todo does check if visit in progress => now check for all users and do not allow for HOPE
  const getPriorVisit = async (visitstatus: string): Promise<boolean> => {
    if (patient) {
      const priorVisitRes = await getPatientInProgressVisit(
        keycloak,
        patient,
        currentUser,
        agency,
        visitstatus,
      );
      if (priorVisitRes) {
        const priorSavedVisit = withLOC(priorVisitRes);
        await checkVisitActive(priorSavedVisit, visitstatus);
        getRolesOptions();
        return true;
      }
    }
    getRolesOptions();
    return false;
  };

  const loadPriorVisitsOnMount = async () => {
    await checkforOtherUserHopeVisits();
    if (!(await getPriorVisit(InProgress))) {
      await getPriorVisit(InProgressDriving);
    }
  };

  const getStandingOrdersHistory = async () => {
    // we want the history of standing orders
    const latest = 'false';
    await observation
      .getPatientStandingOrders(patient?.patientNumber as any, latest, agency)
      .then(async (res: any) => {
        if (!(res instanceof Response)) {
          globalContext.dispatch({
            type: SET_PATIENT_STANDING_ORDERS,
            payload: res,
          });
        }
      });
  };

  const reset = () => {
    // Close the widget first; keep `closingPriorVisit` true until all teardown
    // dispatches finish so VisitInterface does not briefly route to StartVisit
    // while GlobalContext and VisitContext commit at different times.
    dispatch({
      type: OPEN_VISIT_WIDGET,
      payload: null,
    });
    // reset timer
    dispatch({
      type: GET_TIME,
      payload: false,
    });
    dispatch({
      type: SET_PREP_VISIT,
      payload: null,
    });
    dispatch({
      type: SET_SAVED_VISIT,
      payload: null,
    });
    dispatch({
      type: SET_INITIAL_SAVED_VISIT,
      payload: null,
    });
    dispatch({
      type: SET_OPENED_FROM_VISIT_LOG_REOPEN,
      payload: false,
    });
    dispatch({
      type: SET_DOCUMENT_SESSION_STARTED_AT,
      payload: null,
    });
    documentVisitFromColdLoadRef.current = false;
    // Global Context reset
    // undefined for the Loading Text
    globalContext.dispatch({
      type: SET_APP_VISIT,
      payload: undefined,
    });
    globalContext.dispatch({
      type: SET_LEVEL_OF_CARE,
      payload: null,
    });
    dispatch({
      type: SET_ACTING_AS_ROLE,
      payload: null,
    });
    dispatch({
      type: SET_IPU_VISIT,
      payload: false,
    });
    dispatch({
      type: SET_IPU_VISIT_CREATED,
      payload: false,
    });
    dispatch({
      type: SET_MILEAGE,
      payload: null,
    });
    dispatch({
      type: SET_TOLLS_FEES,
      payload: null,
    });
    dispatch({
      type: SET_TRANSIT_FEES,
      payload: null,
    });
    dispatch({
      type: SET_TRAVEL_TIME,
      payload: null,
    });
    dispatch({
      type: SET_IPU_RADIO_CHOICE,
      payload: null,
    });
    setTimeout(() => {
      dispatch({
        type: SET_TIME,
        payload: ['00', '00'],
      });
    }, 200);
    // added to get the just completed visit and reload the context with the data necessary to document to prior etc

    // refresh Patient Visits Log Widget
    visitsGlobalContext.dispatchVG({
      type: SET_PATIENT_VISITS,
      payload: null,
    });
    dispatch({
      type: SET_CLOSING_PRIOR_VISIT,
      payload: false,
    });
  };

  /** Returns false when PIM rejects the update (caller can alert and roll back HOPE state). */
  const updatePatientVisit = async (
    patientNumber: string,
    patientVisit: any,
    hcpcsDTO?: mumms.HCPCSDTO[],
  ): Promise<boolean> => {
    if (hcpcsDTO) {
      patientVisit.physicianServices = hcpcsDTO;
    }

    try {
      const res = await enqueueVisitPut(() =>
        pim.updatePatientVisit(patientNumber, patientVisit as any, agency),
      );
      if (res instanceof Response) {
        return false;
      }
      const savedVisitPayload = hcpcsDTO
        ? { ...res, physicianServices: res.physicianServices ?? hcpcsDTO }
        : res;
      dispatch({
        type: SET_SAVED_VISIT,
        payload: savedVisitPayload,
      });
      const reopenMilestoneKey = (appVisit as any)?.hopeReopenWorkflowMilestone;
      const mergedAppVisit = reopenMilestoneKey
        ? {
            ...savedVisitPayload,
            hopeReopenWorkflowMilestone: reopenMilestoneKey,
          }
        : savedVisitPayload;
      globalContext.dispatch({
        type: SET_APP_VISIT,
        payload: withEffectiveDatetime(mergedAppVisit),
      });
      return true;
    } catch (e) {
      console.error('e', e);
      return false;
    }
  };

  /** Clears in-memory HOPE visit attrs, SIA, and saved flag; optionally clears isHopeVisit. */
  const rollbackHopeVisitAttributes = (options?: {
    clearHopeVisit?: boolean;
  }) => {
    removeVisitAttribute(visitAttributes);
    updateVisitSFVRequired(visitAttributes, false);
    hopeGlobalContext.dispatch({ type: UPDATE_SIA_DATA, payload: {} });
    hopeGlobalContext.dispatch({ type: SET_SIA_TEXT, payload: null });
    hopeGlobalContext.dispatch({ type: SET_SIA_DISPLAY, payload: false });
    hopeGlobalContext.dispatch({
      type: SET_HOPE_VISIT_ATTRIBUTES_SAVED,
      payload: false,
    });
    if (options?.clearHopeVisit) {
      hopeGlobalContext.dispatch({ type: SET_IS_HOPE_VISIT, payload: false });
    }
  };

  /**
   * Applies HOPE/refused visit attributes and saves via PIM.
   * Sets SFVRQ from the SIA scores (cleared when HOPE is refused); Submit Visit re-derives it.
   * When HOPE attrs are already saved and the user is only confirming SIA, PUTs the visit
   * only if SFVRQ changed (SIA is persisted separately via hopepatientsia).
   */
  const persistHopeVisitAttributes = async ({
    isHopeVisit: hopeVisitChoice,
    stateSIA = {},
    openSIAOnSave = false,
  }: PersistHopeVisitAttributesArgs): Promise<boolean> => {
    const {
      availableHOPEActions,
      SIAData,
      hopeVisitAttributesSaved: hopeAttrsAlreadySaved,
    } = hopeGlobalContext;

    if (
      !availableHOPEActions ||
      hopeVisitChoice === null ||
      !appVisit ||
      !patient ||
      !initSavedVisit
    ) {
      return false;
    }

    const currentMilestone = getCurrentMilestone(availableHOPEActions) || '';
    const isSFV = currentMilestone.includes('-SFV');
    const period = isSFV ? defaults.SFVPeriod : defaults.MilestonePeriod;
    const SIAMessage = `${period}${defaults.SIAStatement}`;
    const questionNumber = isSFV ? SFVQuestionNumber : milestoneQuestionNumber;
    const hadAcceptedHope = hopeVisitChoice === true;
    const siaForNote =
      Object.keys(stateSIA).length > 0 ? stateSIA : SIAData || {};

    const applySiaNoteText = () => {
      if (Object.values(siaForNote).length === 8) {
        hopeGlobalContext.dispatch({
          type: SET_SIA_TEXT,
          payload: getSIANote(
            SIAMessage,
            siaForNote as mumms.SIADTO,
            questionNumber,
            currentMilestone,
          ),
        });
      }
    };

    const sfvRequiredBefore = isVisitSFVRequired(visitAttributes);
    const sfvRequired =
      hadAcceptedHope && siaRequiresSfv(siaForNote, currentMilestone);

    // SIA OK after accept: PUT the visit only when SFVRQ changes.
    if (hadAcceptedHope && hopeAttrsAlreadySaved) {
      applySiaNoteText();
      if (sfvRequired !== sfvRequiredBefore) {
        updateVisitSFVRequired(visitAttributes, sfvRequired);
        const sfvSaved = await updatePatientVisit(
          patient.patientNumber as string,
          { ...initSavedVisit, visitAttributes },
        );
        if (!sfvSaved) {
          // Undo only SFVRQ; the HOPE choice is already saved.
          updateVisitSFVRequired(visitAttributes, sfvRequiredBefore);
          dispatch({
            type: SET_VISIT_ALERT,
            payload: {
              text: defaults.hopeVisitSaveError,
              severity: 'error',
            },
          });
          return false;
        }
        visitsGlobalContext.dispatchVG({ type: SET_REFRESH_VISIT });
      }
      if (openSIAOnSave) {
        hopeGlobalContext.dispatch({ type: SET_SIA_DISPLAY, payload: true });
      }
      return true;
    }

    hopeGlobalContext.dispatch({
      type: SET_HOPE_VISIT_ATTRIBUTES_SAVED,
      payload: false,
    });
    updateHopeVisitAttribute(
      visitAttributes,
      currentMilestone,
      hopeVisitChoice,
    );

    applySiaNoteText();

    // SFVRQ follows the SIA scores; refusing HOPE clears it.
    updateVisitSFVRequired(visitAttributes, sfvRequired);

    const saved = await updatePatientVisit(patient.patientNumber as string, {
      ...initSavedVisit,
      visitAttributes,
    });

    if (!saved) {
      rollbackHopeVisitAttributes();
      dispatch({
        type: SET_VISIT_ALERT,
        payload: {
          text: defaults.hopeVisitSaveError,
          severity: 'error',
        },
      });
      return false;
    }

    // Visit Log shows the in-progress visit's HOPE attrs and SFVRQ.
    visitsGlobalContext.dispatchVG({ type: SET_REFRESH_VISIT });

    if (hadAcceptedHope) {
      hopeGlobalContext.dispatch({
        type: SET_HOPE_VISIT_ATTRIBUTES_SAVED,
        payload: true,
      });
      if (openSIAOnSave) {
        hopeGlobalContext.dispatch({ type: SET_SIA_DISPLAY, payload: true });
      }
    }
    return true;
  };

  const refreshVisitWidgets = () => {
    pnGlobalContext.dispatchPN({
      type: REFRESH_PN_DATA,
    });
    globalContext.dispatch({
      type: SET_GLOBAL_MEASURES,
      payload: null,
    });
    globalContext.dispatch({
      type: RELOAD_MANIFEST,
    });
    globalContext.dispatch({
      type: RELOAD_MEASURES,
    });
  };

  const closePatientVisit = async (
    patientNumber: string,
    patientVisit: any,
    hcpcsDTO?: mumms.HCPCSDTO[],
  ) => {
    if (hcpcsDTO) patientVisit.physicianServices = hcpcsDTO;
    return enqueueVisitPut(() =>
      pim.updatePatientVisit(patientNumber, patientVisit as any, agency),
    )
      .then(async (res: any) => {
        if (res instanceof Response) {
          dispatch({
            type: SET_VISIT_ALERT,
            payload: {
              text: defaults.visitSaveError,
              severity: 'error',
            },
          });
          reset();
          return res;
        }
        reset();
        hopeGlobalContext.resetHopeWorkflow();
        // refresh Visit data
        const isSameDateTime =
          initSavedVisit &&
          isSameTime(
            initSavedVisit?.startDate as any,
            patientVisit.startDate as any,
          );

        if (!isSameDateTime) {
          // sync visit date in case it has changed
          await observation
            .syncPatientVisits(patientVisit.id, patientVisit.startDate, agency)
            .then(async (resp: any) => {
              // PN API call and refresh PROGRESS NOTES
              if (!(resp instanceof Response)) {
                refreshVisitWidgets();
              }
            });
        }

        dispatch({
          type: SET_VISIT_ALERT,
          payload: {
            text: defaults.visitSaved,
            severity: 'success',
          },
        });
        return res;
      })
      .catch((e: any) => {
        console.error('e', e);
        dispatch({
          type: SET_VISIT_ALERT,
          payload: {
            text: defaults.visitSaveError,
            severity: 'error',
          },
        });
        return null;
      });
  };

  const closeDocumentingVisit = async () => {
    if (!savedVisit || patient?.patientNumber == null) {
      return;
    }

    dispatch({
      type: SET_CLOSING_PRIOR_VISIT,
      payload: true,
    });
    dispatch({
      type: OPEN_VISIT_WIDGET,
      payload: false,
    });

    try {
      const res = await pim.updatePatientVisit(
        patient.patientNumber,
        {
          ...savedVisit,
          status: Completed,
          reOpened: false,
          documentationTime: getDocumentationTimeForPut(),
        } as any,
        agency,
      );
      if (res instanceof Response) {
        dispatch({
          type: SET_CLOSING_PRIOR_VISIT,
          payload: false,
        });
        dispatch({
          type: OPEN_VISIT_WIDGET,
          payload: true,
        });
        dispatch({
          type: SET_VISIT_ALERT,
          payload: {
            text: defaults.visitSaveError,
            severity: 'error',
          },
        });
        return;
      }
      visitsGlobalContext.dispatchVG({
        type: SET_REFRESH_VISIT,
      });
      reset();
    } catch {
      dispatch({
        type: SET_CLOSING_PRIOR_VISIT,
        payload: false,
      });
      dispatch({
        type: OPEN_VISIT_WIDGET,
        payload: true,
      });
      dispatch({
        type: SET_VISIT_ALERT,
        payload: {
          text: defaults.visitSaveError,
          severity: 'error',
        },
      });
    } finally {
      if (patient?.patientNumber != null && patient?.id != null) {
        // on finishing documentation do unlock the patient in PIM
        unlockPatientInPim({
          patientNumber: patient.patientNumber,
          patientId: patient.id,
        });
      }
    }
  };

  // need to keep map of delete tasks ID
  const [deleteTaskMapping, setTaskMapping] = useState<Record<string, string>>(
    {},
  );

  const updateTaskIcon = (task: mumms.AdminSubTask) => {
    const map = { ...deleteTaskMapping };
    map[task.patientcpptask_id] = task.id;
    setTaskMapping(map);
    setNumTasks(numTasks - 1);
  };

  /**
   * savePatientAdminTask
   */

  const savePatientAdminTask = async (adminTasks: mumms.AdminSubTask[]) => {
    setShowOverlay(true);

    try {
      const res: any = await observation.saveAdminTasks(adminTasks, agency);
      if (res instanceof Response) {
        dispatch({
          type: SET_VISIT_ALERT,
          payload: {
            text: defaults.taskSaveError,
            severity: 'error',
          },
        });
        setShowOverlay(false);
        return false;
      }
      // save the resp for the delete endpoint
      // update the task counts
      const task = res[0];
      updateTaskIcon(task);
      updateVisitTasks();
      // update patient assistance count
      dispatchTask({
        type: UPDATE_WEEKLY_TASKS,
      });
      return true;
    } catch (e) {
      console.error('e', e);
      dispatch({
        type: SET_VISIT_ALERT,
        payload: {
          text: defaults.taskSaveError,
          severity: 'error',
        },
      });
      setShowOverlay(false);
      return false;
    }
  };

  /**
   * deletePatientAdminTask
   */
  const deletePatientAdminTask = async (patientcpptaskid: string) => {
    setShowOverlay(true);

    const id = deleteTaskMapping[patientcpptaskid];
    try {
      const res: any = await observation.deleteAdminTask(id, agency);
      if (res.status === 200) {
        setNumTasks(numTasks + 1);
        updateVisitTasks();
        return true;
      }
      dispatch({
        type: SET_VISIT_ALERT,
        payload: {
          text: defaults.taskDeleteError,
          severity: 'error',
        },
      });
      setShowOverlay(false);
      return false;
    } catch (e) {
      console.error('e', e);
      dispatch({
        type: SET_VISIT_ALERT,
        payload: {
          text: defaults.taskDeleteError,
          severity: 'error',
        },
      });
      setShowOverlay(false);
      return false;
    }
  };

  const updatePNGlobal = async (addNote: mumms.progressNote) => {
    if (progressNotesGlobal && progressNotesGlobal.length) {
      const updatedNotes = [addNote, ...progressNotesGlobal];
      pnGlobalContext.dispatchPN({
        type: SET_PROGRESS_NOTES,
        payload: updatedNotes,
      });
      await delay(100);
      refreshPN();
      await delay(100);
      dispatch({
        type: MINIMIZE_VISIT_DIALOG,
        payload: true,
      });
    }
  };

  const refreshPN = () => {
    if (
      widgetNamesOpen &&
      widgetNamesOpen.length &&
      widgetNamesOpen.includes(ProgressNotesWidget)
    ) {
      // refresh the Progress Note widget by updating the key, no API call
      globalContext.dispatch({
        type: REFRESH_PN_WIDGET,
        payload: true,
      });
    } else {
      openPNWidget();
    }
  };

  const openPNWidget = () => {
    const pnWidget = {
      componentName: ProgressNotesWidget,
    };
    // call to open O/N widget:
    globalContext.dispatch({
      type: OPEN_WIDGET,
      payload: pnWidget,
    });
  };
  const processAttemptedVisit = () => {
    const pnWidget = {
      componentName: ProgressNotesWidget,
    };

    if (ipuRadioChoice) {
      const ipuVisitChoices: any = defaults.attemptedVisitChoice;

      const radiotext = ipuVisitChoices[ipuRadioChoice];
      const attptNote = `${defaults.recordAttemptedVisit} : ${radiotext}`;

      globalContext.dispatch({
        type: SET_ATTEMPTED_VISIT_NOTE,
        payload: attptNote,
      });
    }
    // call to open O/N widget:
    globalContext.dispatch({
      type: OPEN_WIDGET,
      payload: pnWidget,
    });
  };

  /**
   * saveIPUVisitNote
   */
  const saveIPUVisitNote = async (visit: any) => {
    if (ipuRadioChoice) {
      const ipuVisitChoices: any = defaults.ipuVisitChoice;

      const radiotext = ipuVisitChoices[ipuRadioChoice];
      const ipuNote = `${defaults.ipuContactReason} : ${radiotext}`;
      const method = 'POST';
      const ignoreentitydto = 'true';
      const progressNoteData = {
        modifieddatetime: formatPostDate(Date.now()),
        modifiedpersonnumber: currentUser.personNumber,
        note: ipuNote,
        noteddatetime: formatPostDate(visit.startDate),
        visitdatetime: formatPostDate(visit.startDate),
        patientnumber: patient?.patientNumber,
        personnumber: currentUser.personNumber,
        patientprogressnoteentitydto: null,
        siteid: siteId,
        patientvisit_id: visit?.id.toString(),
        isipu: true,
      };
      await observation
        .saveProgressNote(
          progressNoteData as any,
          agency,
          method,
          ignoreentitydto,
        )
        .then(async (res: any) => {
          if (!(res instanceof Response)) {
            dispatch({
              type: SET_IPU_RADIO_CHOICE,
              payload: null,
            });

            updatePNGlobal(res);
          } else {
            dispatch({
              type: SET_VISIT_ALERT,
              payload: {
                text: defaults.visitSaveError,
                severity: 'error',
              },
            });
          }
        })
        .catch((e: any) => {
          console.error('e', e);
          dispatch({
            type: SET_VISIT_ALERT,
            payload: {
              text: defaults.visitSaveError,
              severity: 'error',
            },
          });
        });
    }
  };

  // on Mount to check if status is In Progress
  // on Mount get all standing orders
  useEffect(() => {
    // if not an ADMIN
    if (!isMummsAdmin) {
      // Start Visit already set appVisit/savedVisit then dispatches SET_REFRESH_VISIT
      // so the visit log reloads. Re-running loadPriorVisitsOnMount in that case
      // rediscovers the same visit (HOPE getvisits + getpatientvisits + PIM GET +
      // loadInProgress) and double-fetches CPP tasks. Skip while a session is open;
      // submit/complete callers reset() visit state before (or with) SET_REFRESH_VISIT.
      const hasOpenVisitSession = !!(savedVisit?.id || appVisit?.id);
      if (!hasOpenVisitSession) {
        loadPriorVisitsOnMount();
      }
    }

    // on first loading of dashboard API call to determine if show icon or not
    // when in "Orders and Progress Notes widget" standing orders are added fetch from Global Context
    if (!patientStandingOrdersGlobal) {
      getStandingOrdersHistory();
    }
  }, [visitsGlobalContext.refreshVisits]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleDrivingVisitExpiry = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }

      if (!globalDrivingVisit) {
        return;
      }

      const startDrivingTime = globalDrivingVisit.startdrivingtime;
      const showExpiredModal = () => {
        if (!drivingExpiredModalOpen) {
          dispatchVG({ type: SHOW_DRIVING_EXPIRED_MODAL });
        }
      };

      if (!startDrivingTime || isDrivingVisitExpired(startDrivingTime)) {
        showExpiredModal();
        return;
      }

      if (drivingExpiredModalOpen) {
        return;
      }

      const remainingMs = getDrivingVisitRemainingMs(startDrivingTime);
      if (remainingMs === null || remainingMs <= 0) {
        showExpiredModal();
        return;
      }

      timeoutId = setTimeout(() => {
        dispatchVG({ type: SHOW_DRIVING_EXPIRED_MODAL });
      }, remainingMs);
    };

    scheduleDrivingVisitExpiry();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleDrivingVisitExpiry();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [globalDrivingVisit, drivingExpiredModalOpen, dispatchVG]);

  useEffect(() => {
    if (roleGroupOptions) {
      getVisitOptions();
    }
  }, [roleGroupOptions]);

  useEffect(() => {
    if (widgetsOpen && widgetsOpen.length && !minimizeVisitDialog && appVisit) {
      dispatch({
        type: MINIMIZE_VISIT_DIALOG,
        payload: true,
      });
    }
  }, [widgetsOpen?.length]);

  const updateVisitTasks = async () => {
    getDateRangeTasks();
    setShowOverlay(false);
  };

  const getUserVisitTasks = (pttasks: mumms.PatientCppTask[]) => {
    const userRoleTasks = getUserTasks(pttasks, currentUser);

    setUserTasks(userRoleTasks);
  };

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setTodate] = useState<string | null>(null);

  useEffect(() => {
    // reset dates
    setFromDate(null);
    setTodate(null);
  }, [savedVisit && savedVisit.startDate]);

  const getDateRangeTasks = async () => {
    const startDate = initSavedVisit?.startDate as string;
    const visitDate = moment
      .utc(startDate)
      .tz(siteTimezone)
      .format('MM-DD-YYYY');

    if (fromDate && toDate) {
      const tasks = await observation.getAdminTasks(
        agency,
        siteId as string,
        includedc,
        patient?.patientNumber,
        fromDate,
        toDate,
        visitDate,
      );
      if (!(tasks instanceof Response)) {
        getUserVisitTasks(tasks);
      }
    } else {
      const startDayDate = getStartDate(startDate);
      // 6 days period
      const endDate = moment(startDayDate).subtract(6, 'days');
      const dateTo = startDayDate.format('MM-DD-YYYY');
      const dateFrom = endDate.format('MM-DD-YYYY');
      setFromDate(dateFrom);
      setTodate(dateTo);

      const tasks = await observation.getAdminTasks(
        agency,
        siteId as string,
        includedc,
        patient?.patientNumber,
        dateFrom,
        dateTo,
        visitDate,
      );
      if (!(tasks instanceof Response)) {
        getUserVisitTasks(tasks);
      }
    }
  };

  useEffect(() => {
    if (visitOpen && initSavedVisit) {
      // api call with correct date range
      getDateRangeTasks();
    }
  }, [visitOpen, initSavedVisit]);

  /**
   * Another user's HOPE visit was detected after this user accepted but before attrs saved:
   * roll back local HOPE state and show the in-header warning.
   */
  useEffect(() => {
    if (
      !isOtherInProgHopeVisit ||
      isHopeVisit !== true ||
      hopeVisitAttributesSaved
    ) {
      return;
    }
    rollbackHopeVisitAttributes({ clearHopeVisit: true });
    const el = document.getElementById(visitHeaderID);
    if (el) {
      dispatch({ type: SET_HOPE_VISIT_WARNING, payload: el });
    }
  }, [isOtherInProgHopeVisit, isHopeVisit, hopeVisitAttributesSaved]);

  /**
   * Display the task icon if there are tasks to complete within the visit date range
   */
  useEffect(() => {
    const deleteTaskMappingCopy = {
      ...deleteTaskMapping,
    };

    if (userTasks && userTasks?.length && savedVisit && initSavedVisit) {
      let numberTasks = 0;
      if (userTasks && userTasks?.length && initSavedVisit && savedVisit) {
        userTasks.forEach((task: any) => {
          // Refused or Completed
          const taskDone = isTaskTicked(
            task,
            initSavedVisit.startDate,
            savedVisit?.id,
          );
          if (taskDone) {
            deleteTaskMappingCopy[taskDone.patientcpptask_id] = taskDone.id;
          } else {
            ++numberTasks;
          }
        });
      }
      // update the deleteTaskMapping on load
      setTaskMapping(deleteTaskMappingCopy);
      setNumTasks(numberTasks);
    }
  }, [savedVisit, initSavedVisit, userTasks]);

  return (
    <VisitContext.Provider
      value={{
        visitOpen,
        prepVisit,
        savedVisit,
        initSavedVisit,
        timerDuration,
        timeRequest,
        primaryRole,
        travelMileage,
        transitFees,
        travelDuration,
        tollsFee,
        actingAsRole,
        closePatientVisit,
        updatePatientVisit,
        savePatientAdminTask,
        deletePatientAdminTask,
        reset,
        roleGroups: roleGroupOptions,
        visitTypeOptions: visitOptions,
        visitStartTimer,
        minimizeVisitDialog,
        visitTitle,
        editTime,
        ccTime,
        visitError,
        userTasks,
        showTasks: showTasks && !timeRequest,
        alertVisitInfo,
        numPatientTasks: numTasks,
        showOverlay,
        dispatchVisit: dispatch,
        IPUVisit,
        editFaceToFace,
        saveIPUVisitNote,
        processAttemptedVisit,
        ipuRadioChoice,
        canDeleteOpenVisit,
        deleteOpenVisit,
        is30MinAgency,
        showConfirmVisit,
        confirmVisitMissingDoc,
        confirmVisitMissingHCPCS,
        stopVisit,
        tasksToBeSaved,
        tasksToBeDeleted,
        hopeVisitWarning,
        checkforOtherUserHopeVisits,
        persistHopeVisitAttributes,
        visitEpisode,
        hydrateInProgressVisitFromLog,
        hydrateDocumentVisitFromLog,
        openedFromVisitLogReopen,
        getDocumentSessionMinutes,
        getDocumentationTimeForPut,
        closeDocumentingVisit,
        closingPriorVisit,
      }}
    >
      <>
        {children}
        <DrivingVisitExpiredModal
          open={drivingExpiredModalOpen}
          onConfirm={confirmDeleteExpiredDrivingVisit}
          confirming={deletingDrivingVisit}
        />
      </>
    </VisitContext.Provider>
  );
};

export default VisitContextProvider;
